import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import TablePagination from "@material-ui/core/TablePagination";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";

export const COMMON_PAGINATION_CLASS = "common-table-pagination";

export const commonPaginationSelectProps = {
  inputProps: {
    "aria-label": "rows per page",
  },
  native: false,
  variant: "outlined",
  MenuProps: {
    disableScrollLock: true,
    classes: {
      paper: "common-pagination-menu-paper",
    },
  },
};

export const getCommonPaginationStyle = (isDarkMode = false) => ({
  color: isDarkMode ? "#f5f7fb" : "#1e293b",
  borderTop: isDarkMode ? "1px solid rgba(148, 163, 184, 0.25)" : "1px solid #e2e8f0",
  backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.88)" : "#ffffff",
});

export const getCommonPaginationSx = (isDarkMode = false) => ({
  color: isDarkMode ? "#f5f7fb" : "#1e293b",
  borderTop: isDarkMode ? "1px solid rgba(148, 163, 184, 0.25)" : "1px solid #e2e8f0",
  backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.88)" : "#ffffff",
});

const PAGINATION_SCROLL_TOP_OFFSET = 96;

const getPaginationScrollTarget = (wrapperElement) => {
  if (!wrapperElement) {
    return null;
  }

  const bottomBar = wrapperElement.closest(".shared-table-bottom-bar");

  if (bottomBar?.previousElementSibling) {
    return bottomBar.previousElementSibling;
  }

  return (
    wrapperElement.closest(".dashboard-attendance-table") ||
    wrapperElement.closest(".MuiTableContainer-root") ||
    wrapperElement.parentElement
  );
};

const scrollPaginationTargetIntoView = (wrapperElement) => {
  const targetElement = getPaginationScrollTarget(wrapperElement);

  if (!targetElement) {
    return;
  }

  const targetTop = targetElement.getBoundingClientRect().top + window.pageYOffset - PAGINATION_SCROLL_TOP_OFFSET;

  window.scrollTo({
    top: Math.max(targetTop, 0),
    behavior: "auto",
  });
};

export function CommonTablePagination(props) {
  const {
    isDarkMode = false,
    className = "",
    SelectProps,
    style,
    sx,
    rowsPerPageOptions = [10, 25, 50],
    ActionsComponent = CommonTablePaginationActions,
    onPageChange,
    onChangePage,
    onRowsPerPageChange,
    onChangeRowsPerPage,
    ...rest
  } = props;

  const wrapperRef = useRef(null);
  const pendingRowsPerPageScrollRef = useRef(false);
  const resolvedOnPageChange = onPageChange || onChangePage;
  const resolvedOnRowsPerPageChange = onRowsPerPageChange || onChangeRowsPerPage;

  useEffect(() => {
    if (!pendingRowsPerPageScrollRef.current || !wrapperRef.current) {
      return;
    }

    pendingRowsPerPageScrollRef.current = false;

    window.requestAnimationFrame(() => {
      scrollPaginationTargetIntoView(wrapperRef.current);
    });
  }, [rest.rowsPerPage]);

  const handleRowsPerPageChange = (event) => {
    pendingRowsPerPageScrollRef.current = true;

    if (typeof resolvedOnRowsPerPageChange === "function") {
      resolvedOnRowsPerPageChange(event);
    }
  };

  return (
    <div ref={wrapperRef}>
      <TablePagination
        {...rest}
        component={rest.component || "div"}
        rowsPerPageOptions={rowsPerPageOptions}
        SelectProps={{
          ...commonPaginationSelectProps,
          ...SelectProps,
        }}
        className={`${COMMON_PAGINATION_CLASS} ${className}`.trim()}
        style={{
          ...getCommonPaginationStyle(isDarkMode),
          ...style,
        }}
        sx={sx}
        onPageChange={resolvedOnPageChange}
        onChangePage={resolvedOnPageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        ActionsComponent={ActionsComponent}
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function CommonTablePaginationActions(props) {
  const classes = useStyles();
  const theme = useTheme();
  const { count, page, rowsPerPage } = props;
  const onPageChange = props.onPageChange || props.onChangePage;
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const handlePageChange = (event, nextPage) => {
    if (typeof onPageChange === "function") {
      onPageChange(event, nextPage);
    }
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={(event) => handlePageChange(event, 0)}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={(event) => handlePageChange(event, page - 1)}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(event) => handlePageChange(event, page + 1)}
        disabled={page >= lastPage}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={(event) => handlePageChange(event, lastPage)}
        disabled={page >= lastPage}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

CommonTablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func,
  onPageChange: PropTypes.func,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

CommonTablePagination.propTypes = {
  ActionsComponent: PropTypes.elementType,
  SelectProps: PropTypes.object,
  className: PropTypes.string,
  component: PropTypes.elementType,
  count: PropTypes.number.isRequired,
  isDarkMode: PropTypes.bool,
  onChangePage: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.array,
  style: PropTypes.object,
  sx: PropTypes.object,
};

export default CommonTablePaginationActions;
